import React from 'react';
import { Table, Code, Share2, Play, Settings, User, AlertTriangle, MousePointerClick, Info, ArrowUp, Globe, BarChart2, Layout, Image } from 'lucide-react';

export const SimulatedSheet = () => (
    <div className="w-full bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden font-sans text-xs sm:text-sm relative group">
        <div className="bg-google-green text-white p-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <Table size={16} />
                <span className="font-semibold">My Database</span>
            </div>
            <span className="text-[10px] bg-green-600 px-2 py-0.5 rounded">read-only view</span>
        </div>

        {/* Visual Guide Overlay */}
        <div className="absolute top-12 -left-2 bg-yellow-100 border border-yellow-300 text-yellow-800 px-2 py-1 rounded text-[10px] shadow-sm transform -rotate-6 z-10 hidden group-hover:block md:block animate-bounce">
            Row 1 = Headers (Keys) 🔑
        </div>
        <div className="absolute top-24 -left-2 bg-blue-100 border border-blue-300 text-blue-800 px-2 py-1 rounded text-[10px] shadow-sm transform -rotate-3 z-10 hidden group-hover:block md:block">
            Row 2+ = Data (Values) 📦
        </div>

        <div className="grid grid-cols-[40px_1fr_1fr_1fr] border-b border-gray-200 bg-gray-50 font-bold text-gray-600 text-center">
            <div className="p-2 border-r border-gray-200 bg-gray-100"></div>
            <div className="p-2 border-r border-gray-200">A</div>
            <div className="p-2 border-r border-gray-200">B</div>
            <div className="p-2">C</div>
        </div>

        {/* Row 1: Headers */}
        <div className="grid grid-cols-[40px_1fr_1fr_1fr] border-b border-gray-300 bg-yellow-50 relative">
            <div className="p-2 border-r border-gray-300 font-bold bg-gray-100 text-center text-gray-500">1</div>
            <div className="p-2 border-r border-gray-200 font-bold text-black flex items-center justify-center gap-1">
                Name
            </div>
            <div className="p-2 border-r border-gray-200 font-bold text-black flex items-center justify-center gap-1">
                Power
            </div>
            <div className="p-2 font-bold text-black flex items-center justify-center gap-1">
                Level
            </div>

            {/* Header Indicator Line */}
            <div className="absolute inset-0 border-2 border-yellow-400 pointer-events-none opacity-50"></div>
        </div>

        {/* Row 2: Data */}
        <div className="grid grid-cols-[40px_1fr_1fr_1fr] border-b border-gray-100 text-gray-600">
            <div className="p-2 border-r border-gray-200 bg-gray-50 text-center text-gray-500 font-medium">2</div>
            <div className="p-2 border-r border-gray-100 text-center">Pikachu</div>
            <div className="p-2 border-r border-gray-100 text-center">Lightning</div>
            <div className="p-2 text-center">9000</div>
        </div>

        {/* Row 3: Data */}
        <div className="grid grid-cols-[40px_1fr_1fr_1fr] border-b border-gray-100 text-gray-600">
            <div className="p-2 border-r border-gray-200 bg-gray-50 text-center text-gray-500 font-medium">3</div>
            <div className="p-2 border-r border-gray-100 text-center">Mario</div>
            <div className="p-2 border-r border-gray-100 text-center">Jump</div>
            <div className="p-2 text-center">500</div>
        </div>

        {/* Row 4: Data */}
        <div className="grid grid-cols-[40px_1fr_1fr_1fr] text-gray-600">
            <div className="p-2 border-r border-gray-200 bg-gray-50 text-center text-gray-500 font-medium">4</div>
            <div className="p-2 border-r border-gray-100 text-center">Sonic</div>
            <div className="p-2 border-r border-gray-100 text-center">Speed</div>
            <div className="p-2 text-center">9001</div>
        </div>
    </div>
);

export const SimulatedFormulaSheet = () => (
    <div className="w-full bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden font-sans text-xs relative animate-fade-in-up">
        {/* Header Grid */}
        <div className="grid grid-cols-[30px_1fr_1fr] border-b border-gray-200 bg-gray-50 font-bold text-gray-500 text-center">
            <div className="p-1 border-r bg-gray-100"></div>
            <div className="p-1 border-r">A (Score)</div>
            <div className="p-1">B (Result)</div>
        </div>

        <div className="grid grid-cols-[30px_1fr_1fr] border-b border-gray-100">
            <div className="p-2 bg-gray-50 border-r text-center font-bold text-gray-400">1</div>
            <div className="p-2 border-r text-center">85</div>
            <div className="p-2 text-center font-bold text-green-600">Pass</div>
        </div>
        <div className="grid grid-cols-[30px_1fr_1fr] border-b border-gray-100">
            <div className="p-2 bg-gray-50 border-r text-center font-bold text-gray-400">2</div>
            <div className="p-2 border-r text-center">40</div>
            <div className="p-2 text-center font-bold text-red-500">Fail</div>
        </div>

        <div className="p-3 bg-blue-50 text-blue-800 text-xs font-mono text-center border-t border-blue-100">
            Formula used: <br /><strong>=IF(A1 {'>'} 50, "Pass", "Fail")</strong>
        </div>
    </div>
);

export const SimulatedNativeSheet = ({ mode }: { mode: number }) => (
    <div className="w-full bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden font-sans text-xs relative animate-fade-in-up">
        {/* Header Grid */}
        <div className="grid grid-cols-[30px_1fr_1fr] border-b border-gray-200 bg-gray-50 font-bold text-gray-500 text-center">
            <div className="p-1 border-r bg-gray-100"></div>
            <div className="p-1 border-r">A</div>
            <div className="p-1">B</div>
        </div>

        {/* IMPORTXML MODE */}
        {mode === 1 && (
            <>
                <div className="grid grid-cols-[30px_1fr_1fr] border-b border-gray-100">
                    <div className="p-2 bg-gray-50 border-r text-center font-bold text-gray-400">1</div>
                    <div className="p-2 border-r text-gray-400 italic">=IMPORTXML(...)</div>
                    <div className="p-2 text-gray-400"></div>
                </div>
                <div className="grid grid-cols-[30px_1fr_1fr] bg-blue-50">
                    <div className="p-2 bg-gray-50 border-r text-center font-bold text-gray-400">2</div>
                    <div className="p-2 border-r font-bold text-blue-800 col-span-2 flex items-center gap-2">
                        <Globe size={12} /> Wikipedia: The Moon
                    </div>
                </div>
            </>
        )}

        {/* SPARKLINE MODE */}
        {mode === 2 && (
            <>
                <div className="grid grid-cols-[30px_1fr_1fr] border-b border-gray-100">
                    <div className="p-2 bg-gray-50 border-r text-center font-bold text-gray-400">1</div>
                    <div className="p-2 border-r text-center">Data: 10, 50, 30</div>
                    <div className="p-2 text-center relative h-10 flex items-center justify-center">
                        <div className="flex items-end gap-1 h-6">
                            <div className="w-2 bg-orange-400 h-[20%]"></div>
                            <div className="w-2 bg-orange-400 h-[100%]"></div>
                            <div className="w-2 bg-orange-400 h-[60%]"></div>
                        </div>
                    </div>
                </div>
            </>
        )}

        {/* TRANSLATE MODE */}
        {mode === 3 && (
            <>
                <div className="grid grid-cols-[30px_1fr_1fr] border-b border-gray-100">
                    <div className="p-2 bg-gray-50 border-r text-center font-bold text-gray-400">1</div>
                    <div className="p-2 border-r font-bold">Hello World</div>
                    <div className="p-2 font-bold text-purple-600">Hola Mundo</div>
                </div>
                <div className="grid grid-cols-[30px_1fr_1fr] border-b border-gray-100">
                    <div className="p-2 bg-gray-50 border-r text-center font-bold text-gray-400">2</div>
                    <div className="p-2 border-r font-bold">Thank You</div>
                    <div className="p-2 font-bold text-purple-600">Gracias</div>
                </div>
            </>
        )}

    </div>
);

export const SimulatedEditor = ({ code }: { code?: string }) => (
    <div className="w-full bg-[#1e1e1e] rounded-lg shadow-md overflow-hidden text-xs sm:text-sm font-mono">
        <div className="bg-[#2d2d2d] text-gray-300 p-2 flex items-center justify-between border-b border-gray-700">
            <div className="flex items-center gap-2">
                <Code size={14} className="text-orange-400" />
                <span>Code.gs</span>
            </div>
            <div className="flex gap-2">
                <div className="bg-blue-600 px-2 py-0.5 rounded text-white text-[10px]">Save</div>
                <div className="bg-blue-600 px-2 py-0.5 rounded text-white text-[10px] flex items-center gap-1">
                    <Play size={8} fill="currentColor" /> Run
                </div>
            </div>
        </div>
        <div className="p-4 text-gray-300 max-h-64 overflow-y-auto">
            {code ? (
                <pre className="text-[10px] leading-tight text-blue-300 whitespace-pre-wrap">{code}</pre>
            ) : (
                <>
                    <div className="text-gray-500">// Your system logic goes here</div>
                    <div className="text-purple-400">function <span className="text-yellow-200">doGet</span>() {'{'}</div>
                    <div className="pl-4 text-green-400">return <span className="text-orange-300">"Hello World"</span>;</div>
                    <div>{'}'}</div>
                </>
            )}
        </div>
    </div>
);

export const SimulatedAuth = () => (
    <div className="w-full flex flex-col items-center justify-center space-y-4 font-sans p-2">
        {/* Step 1: Review Permissions */}
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-300 w-64 text-xs relative opacity-50 scale-90 origin-bottom">
            <h3 className="font-bold mb-2">Authorization Required</h3>
            <button className="bg-blue-600 text-white font-bold py-1 px-3 rounded text-[10px] mb-1">Review permissions</button>
        </div>

        <div className="text-gray-300 font-bold text-xl">⬇</div>

        {/* Step 2: The Scary Warning */}
        <div className="bg-white p-4 rounded-lg shadow-xl border-l-4 border-red-500 w-72 text-sm relative">
            <div className="flex items-center gap-2 text-red-600 mb-2 font-bold">
                <AlertTriangle size={16} /> Google hasn't verified this app
            </div>
            <p className="text-gray-500 text-xs mb-3">This is normal! Click 'Advanced' to continue.</p>

            <div className="space-y-2">
                <div className="text-gray-500 text-xs hover:bg-gray-100 p-1 rounded cursor-pointer font-semibold underline">
                    Advanced
                </div>

                <div className="bg-red-50 p-2 rounded border border-red-100 flex items-center justify-between group cursor-pointer hover:bg-red-100 transition-colors">
                    <span className="text-[10px] text-gray-600">Go to Untitled Project (unsafe)</span>
                    <MousePointerClick className="text-red-500 animate-bounce" size={16} />
                </div>
            </div>
        </div>
    </div>
);

export const SimulatedDeploy = () => (
    <div className="w-full bg-white rounded-lg shadow-md border border-gray-200 p-4 font-sans text-sm">
        <div className="flex justify-between items-center mb-4 border-b pb-2">
            <h3 className="font-bold text-gray-700">New Deployment</h3>
            <Settings size={16} className="text-gray-400" />
        </div>

        <div className="space-y-3">
            <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Execute as</label>
                <div className="flex items-center gap-2 p-2 bg-yellow-50 text-yellow-800 rounded border border-yellow-300 font-bold animate-pulse">
                    <User size={14} /> Me (your_email@gmail.com)
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Who has access</label>
                <div className="p-2 border border-red-300 bg-red-50 rounded text-red-700 font-bold flex items-center gap-2">
                    <Share2 size={14} /> Anyone
                </div>
                <p className="text-[10px] text-gray-400 mt-1">* Very Important!</p>
            </div>

            <div className="bg-blue-600 text-white text-center py-2 rounded font-bold shadow-sm mt-2">
                Deploy
            </div>
        </div>
    </div>
);

export const SimulatedSite = ({ step }: { step: number }) => (
    <div className="w-full h-full bg-white shadow-lg rounded-t-lg overflow-hidden flex flex-col">
        {/* Fake Browser Toolbar */}
        <div className="bg-gray-100 border-b border-gray-200 p-2 flex items-center gap-2">
            <div className="flex gap-1">
                <div className="w-3 h-3 rounded-full bg-red-300"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-300"></div>
            </div>
            <div className="bg-white border border-gray-200 rounded px-2 text-[10px] text-gray-400 flex-1">
                https://sites.google.com/new
            </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative bg-white">
            {/* Google Sites UI Simulator */}
            <div className="h-24 bg-teal-800 text-white flex items-end p-4">
                <h1 className="text-2xl font-bold">My Awesome Site</h1>
            </div>

            <div className="p-4 grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-4">
                    <div className="h-32 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                        {step >= 3 ? (
                            <div className="bg-green-50 w-full h-full p-2 border border-green-200 text-xs">
                                <div className="font-bold text-green-800 mb-1">🌱 My Live Database Data</div>
                                <div className="bg-white p-1 mb-1 rounded border border-green-100">
                                    <strong>Pikachu</strong>: Lightning
                                </div>
                                <div className="bg-white p-1 mb-1 rounded border border-green-100">
                                    <strong>Mario</strong>: Jump
                                </div>
                            </div>
                        ) : (
                            <span>[ Content Area ]</span>
                        )}
                    </div>
                    <div className="h-20 bg-gray-50 rounded-lg"></div>
                </div>
                <div className="col-span-1 bg-gray-50 rounded-lg p-2 space-y-2 border-l border-gray-100">
                    <div className="text-[10px] font-bold text-gray-500 uppercase">Insert</div>
                    <div className="flex flex-col gap-2">
                        <div className="bg-white p-2 text-center text-xs border border-gray-200 rounded shadow-sm">Text Box</div>
                        <div className="bg-white p-2 text-center text-xs border border-gray-200 rounded shadow-sm">Images</div>
                        <div className={`bg-white p-2 text-center text-xs border-2 rounded shadow-sm flex items-center justify-center gap-1 ${step === 3 ? 'border-teal-500 text-teal-700 animate-pulse' : 'border-gray-200'}`}>
                            <Code size={12} /> Embed
                        </div>
                    </div>
                </div>
            </div>

            {/* Overlay for steps */}
            {step === 1 && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center">
                    <div className="bg-teal-600 text-white px-4 py-2 rounded-full font-bold shadow-xl animate-bounce">
                        Click 'Blank Site' to start
                    </div>
                </div>
            )}
        </div>
    </div>
);
